import { bffUpload, legacy } from '@/shared/api/client';
import { parseLegacyId } from '@/shared/lib/legacy-id';
import type {
  AdminClient,
  AdminStore,
  ClientContact,
  ClientDetailResponse,
  ClientFilteredStats,
  ClientGridCard,
  ClientKind,
  ClientLifecycleStatus,
  ClientUser,
} from '@/shared/types/client';

import { passbookLogoUrl } from '@/shared/lib/passbook-logo';

function normalizeContact(contact: ClientContact): ClientContact {
  return {
    _id: contact._id ? parseLegacyId(contact._id) : undefined,
    name: contact.name ?? '',
    phone: contact.phone ?? '',
    email: contact.email ?? '',
    role: contact.role ?? '',
  };
}

function normalizeClient(client: AdminClient): AdminClient {
  return {
    ...client,
    lifecycleStatus: (client.lifecycleStatus ?? 'prospect') as ClientLifecycleStatus,
    clientKind: (client.clientKind ?? 'fixe') as ClientKind,
    registrationStatus: client.registrationStatus ?? 'approved',
    contacts: (client.contacts ?? []).map(normalizeContact),
    passbooks: (client.passbooks ?? []).map((pb) => ({
      ...pb,
      _id: parseLegacyId(pb._id),
      stats: pb.stats ?? { cadastros: 0, ios: 0, android: 0 },
    })),
  };
}

export async function fetchClients(): Promise<AdminClient[]> {
  const data = await legacy.get<AdminClient[]>('/clients');
  return Array.isArray(data) ? data.map(normalizeClient) : [];
}

export async function fetchClientDetail(id: string): Promise<{ client: AdminClient; user: ClientUser }> {
  const { client } = await legacy.get<ClientDetailResponse>(`/client/${id}`);
  const normalized = normalizeClient(client);
  const userId = normalized.users?.[0];
  if (!userId) return { client: normalized, user: { _id: '', email: '' } };
  const user = await legacy.get<ClientUser>(`/user/${userId}`);
  return { client: normalized, user };
}

export async function toggleClientInactive(id: string) {
  await legacy.get(`/client/inactive/${id}`);
}

export async function approveClientDraft(id: string) {
  return legacy.post<{ status: string; contract?: AdminClient['contract'] }>(`/client/${id}/approve`);
}

export async function refreshContractStatus(id: string) {
  return legacy.get<{ status: string; contract?: AdminClient['contract'] }>(`/client/${id}/contract-status`);
}

export async function deleteClient(id: string, userId: string) {
  await legacy.delete(`/client/${id}/${userId}`);
}

export interface ClientFormPayload {
  action: 'insert' | 'edit';
  name: string;
  email: string;
  password: string;
  position: string;
  cnpj: string;
  name_client: string;
  email_client: string;
  phone?: string;
  zipcode?: string;
  address?: string;
  number?: string;
  sector?: string;
  type?: string;
  site?: string;
  value_pay?: string;
  models?: string[];
  pdv?: string;
  lifecycleStatus?: ClientLifecycleStatus;
  clientKind?: ClientKind;
  contacts?: ClientContact[];
  id_user?: string;
  cpnj_verify?: string;
}

export async function saveClient(payload: ClientFormPayload) {
  const hasExistingUser = Boolean(payload.action === 'edit' && payload.id_user);
  // Draft público: cliente já existe (edit), mas o usuário de acesso ainda não —
  // o register precisa ser insert; o /client continua edit pelo CNPJ.
  const registerBody = {
    action: hasExistingUser ? 'edit' : 'insert',
    name: payload.name,
    email: payload.email,
    password: payload.password,
    position: payload.position,
    cnpj: payload.cnpj,
    role: 'Client',
    validated: true,
    ...(hasExistingUser ? { id: payload.id_user } : {}),
  };

  const registerRes = await legacy.post<{ user: { _id: string } }>('/auth/register', registerBody);
  const idUser = hasExistingUser ? payload.id_user! : registerRes.user._id;

  const { models, contacts, ...rest } = payload;

  await legacy.post('/client', {
    ...rest,
    models: models?.length ? models.join(',') : undefined,
    contacts: JSON.stringify(contacts ?? []),
    id_user: idUser,
    cpnj_verify: payload.cpnj_verify ?? payload.cnpj,
  });
}

export interface ClientCloneResult {
  client: { _id: string; cnpj: string; name: string; email: string };
  passbook: { _id: string; slug: string; shortUrl: string };
  user: { _id: string; email: string; generatedPassword: string };
  warnings: string[];
}

/**
 * Clona um client (+ passbook embutido + user de login) a partir de um
 * client existente com exatamente 1 passbook. `formData` traz os campos
 * novos (cnpj, name, email, slug, cores, organizationName) + o .zip com
 * as imagens — tudo processado atomicamente por `POST /client/:id/clone`
 * na legacy-api. Ver `POST /bff/uploads/client/:id/clone` no BFF.
 */
export async function cloneClient(id: string, formData: FormData): Promise<ClientCloneResult> {
  return bffUpload<ClientCloneResult>(`/bff/uploads/client/${id}/clone`, formData);
}

/** Lojas já cadastradas no passbook de origem — usado pra pré-preencher o
 * 2º step da clonagem (o operador revisa/ajusta antes de confirmar). */
export async function fetchPassbookStores(idPassbook: string): Promise<AdminStore[]> {
  const data = await legacy.get<AdminStore[]>(`/stores/${idPassbook}`);
  return Array.isArray(data) ? data : [];
}

/** Cria em lote as lojas do passbook recém-clonado. Array vazio é aceito
 * (o operador pode pular esta etapa) e não grava nada. */
export async function createStores(
  idPassbook: string,
  stores: AdminStore[],
): Promise<{ created: number }> {
  return legacy.post<{ created: number }>('/stores', { idPassbook, stores });
}

export function computeClientStats(clients: AdminClient[]): ClientFilteredStats {
  let totalCadastros = 0;
  let totalIos = 0;
  let totalAndroid = 0;

  for (const client of clients) {
    for (const passbook of client.passbooks ?? []) {
      if (passbook.onlyShortUrl) continue;
      const stats = passbook.stats;
      if (!stats) continue;
      totalCadastros += stats.cadastros;
      totalIos += stats.ios;
      totalAndroid += stats.android;
    }
  }

  return {
    totalClients: clients.length,
    totalCadastros,
    totalIos,
    totalAndroid,
  };
}

export function buildClientGridCards(clients: AdminClient[]): ClientGridCard[] {
  const cards: ClientGridCard[] = [];

  for (const client of clients) {
    const passbooks = (client.passbooks ?? []).filter((p) => !p.onlyShortUrl);
    // Grupo com várias marcas no mesmo client: o nome do client repetiria em
    // todos os cards, então cada um mostra o nome do próprio programa.
    const hasManyPassbooks = passbooks.length > 1;
    for (const passbook of passbooks) {
      cards.push({
        id: client._id,
        name: (hasManyPassbooks && passbook.organizationName?.trim()) || client.name,
        passbookId: passbook._id,
        logoUrl: passbookLogoUrl(passbook._id),
        backgroundColor: passbook.backgroundColor,
        lifecycleStatus: client.lifecycleStatus,
        clientKind: client.clientKind,
        inactive: client.inactive,
      });
    }
  }

  return cards.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
}

export const CLIENT_LIFECYCLE_LABELS: Record<ClientLifecycleStatus, string> = {
  cliente: 'Cliente',
  prospect: 'Prospect',
};

export const CLIENT_KIND_LABELS: Record<ClientKind, string> = {
  fixe: 'Fixe',
  parceiro: 'Parceiro',
  whitelabel: 'Whitelabel',
};
